import { useStore } from "@nanostores/preact"
import classNames from "utils/preact/class-names"

import { useTranslation } from "components/signup/components/Translation"
import { profile, SubscriptionValue } from "components/signup/services/store"
import { ProductDuration, savePercent } from "components/signup/services/support"
import { SubscriptionButtons } from "components/signup/components/SubscriptionButtons"

import { useUserData } from "components/signup/hooks/useUserData"
import { Logo } from "../components/Logo"
import { PremiumBenefits } from "../components/PremiumBenefits"

import css from "../style.module.scss"
import speedIcon from "../static/speed.svg"
import removeIcon from "../static/remove.svg"
import extendIcon from "../static/extend.svg"

function ViewA({ onSubmit }: { onSubmit: (v: SubscriptionValue) => void }) {
  const t = useTranslation()
  const user = useStore(profile)
  const { currency, products } = useUserData()

  const annualSavePercent = savePercent(products, currency.acronym)

  return (
    <div className={css.content}>
      <Logo />
      <PremiumBenefits />
      <div className={css["offers-container"]}>
        {user.subscription === "annually" && (
          <span className={css["offers-badge"]}>
            {t("save_percent", { percent: annualSavePercent })}
          </span>
        )}
        {user.subscription === "monthly" && (
          <span className={css["offers-badge"]}>{t("most_flexible")}</span>
        )}

        <form
          className={css["offers-form"]}
          onSubmit={(e) => {
            e.preventDefault()
            onSubmit(user.subscription)
          }}
        >
          <SubscriptionButtons />
          <button className={css.submit} type="submit" data-session-recording="continue">
            {t("continue")}
          </button>
        </form>
      </div>
    </div>
  )
}

function ViewB({ onSubmit }: { onSubmit: (v: SubscriptionValue) => void }) {
  const t = useTranslation()
  const { currency, products } = useUserData()

  if (!currency || !products) {
    return <div>Loading subscription options...</div>
  }

  return (
    <div className={css.content}>
      <div className={css["access-block"]}>
        <div
          className={classNames({
            [css["font-heavy"]]: true,
            [css["title-incentive"]]: true,
            [css["text-center"]]: true,
            [css["spacer-bottom"]]: true,
            [css["text-blue"]]: true,
          })}
        >
          {t("get_full_access")}
        </div>

        <ul className={css["benefits-list"]}>
          <li className={css["font-medium"]}>
            <img src={removeIcon} alt="Remove Icon" className={css.icon} />
            {t("remove_ads")}
          </li>
          <li className={css["font-medium"]}>
            <img src={speedIcon} alt="speed Icon" className={css.icon} />
            {t("faster_site")}
          </li>
          <li className={css["font-medium"]}>
            <img src={extendIcon} alt="Extended Icon" className={css.icon} />
            {t("extended_forecasts")}
          </li>
        </ul>

        <div className={css["spacer-bottom"]} />

        {["monthly", "annually"].map((type: ProductDuration) => {
          const price = products[type].prices[currency.acronym]
          return (
            <button
              type="button"
              className={classNames({
                [css["subscription-button"]]: true,
                [css["is-primary"]]: true,
              })}
              onClick={() => onSubmit(type)}
              data-session-recording="offer"
            >
              <span className={css["subscription-name"]}>{t(`support_${type}`)}</span>
              <span className={css["subscription-price"]}>
                {currency.prefix}&thinsp;{price.unit_amount / 100}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

enum Experiments {
  A,
  B,
}

export function Subscriptions({
  experiment,
  onSubmit,
}: {
  experiment: number
  onSubmit: (v: SubscriptionValue) => void
}) {
  switch (experiment) {
    case Experiments.A:
      return (
        <ViewA
          onSubmit={async (v: SubscriptionValue) => {
            profile.setKey("subscription", v)
            onSubmit(v)
          }}
        />
      )
    case Experiments.B:
      return (
        <ViewB
          onSubmit={async (v: SubscriptionValue) => {
            profile.setKey("subscription", v)
            onSubmit(v)
          }}
        />
      )
    default:
      // eslint-disable-next-line no-console
      console.error(
        "Failed while trying to determine which page to show. Unknown value:",
        experiment,
      )
      break
  }
}
